import { FormCard, FormItem, Input, Select, TextArea } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { InputNumber } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import PatternSelectEmptyState from '../components/PatternSelectEmptyState'
import useAllocationList from '../hooks/useAllocationList'
import { useCarersClockedInValidator } from '../hooks/useCarersClockedInValidator'
import { getRequiredRule, getTeamMembersClockInsRule } from '../utils'
import InputSuffix from './InputSuffix'
import { TeamMembersMultiSelect } from './TeamMembersMultiSelect'
import {
  cleanDecimalValues,
  convertAllocationListToGroupedOptions
} from './utils'

type AboutVisitProps = {
  typeOfVisitOptions?: DefaultOptionType[]
  hideTeamMembers?: boolean
  hideAboutSection?: boolean
  showTeamMembersSelect?: boolean
  formData?: Record<string, any> | undefined
}

const AboutVisit: FC<AboutVisitProps> = ({
  typeOfVisitOptions,
  hideTeamMembers,
  hideAboutSection,
  showTeamMembersSelect,
  formData
}) => {
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['rostering_editing_limitations']
  })?.data

  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled

  const instanceId = formData?.id || formData?.eventId

  // Use allocation list API (like quick allocation) to get users with clock-in status
  // This automatically filters out clocked-in users when availability filter is used
  const { allocationList } = useAllocationList({
    id: instanceId,
    allocationListFilters: flagsRosteringEditingLimitations
      ? {
          availability: ['Available and Selected']
        }
      : undefined,
    skipPagination: true
  })

  // Fallback to grouped carers if no instanceId (new events)
  const { groupedCarerOptions: fallbackGroupedCarerOptions } =
    useGroupedCarers()

  // Convert allocation list to grouped options format, or use fallback
  // Only use allocation list if it has data, otherwise use fallback
  const groupedCarerOptions = useMemo(() => {
    // If allocation list is loading or empty, use fallback
    if (!allocationList || allocationList.length === 0) {
      return fallbackGroupedCarerOptions
    }

    return convertAllocationListToGroupedOptions(
      allocationList,
      fallbackGroupedCarerOptions
    )
  }, [allocationList, fallbackGroupedCarerOptions])

  const teamMembersCount = formData?.teamMembers?.length ?? 0
  const maxTeamMembers = formData?.numberSupply ?? 0

  // Get original team member IDs from formData (set in EditReqModal)
  const originalTeamMemberIds = formData?.originalTeamMemberIds

  // Use formData?.id or formData?.eventId, and pass original team members
  const { validator: validateTeamMembers } = useCarersClockedInValidator(
    formData?.id || formData?.eventId,
    originalTeamMemberIds
  )

  return (
    <FormCard id="rota-about-visit-form" level={2} title={t('ABOUT_THE_VISIT')}>
      {!hideAboutSection && (
        <>
          <FormItem
            name="instanceTypeId"
            label={<Text>{t('TYPE_OF_VISIT')}</Text>}
            rules={[getRequiredRule(t('TYPE_OF_VISIT'))]}
          >
            <Select
              options={typeOfVisitOptions}
              notFoundContent={
                <PatternSelectEmptyState link="/settings/rota-patterns" />
              }
            />
          </FormItem>
          <FormItem
            name="title"
            label={<Text>{t('TITLE')}</Text>}
            rules={[getRequiredRule(t('TITLE'))]}
          >
            <Input />
          </FormItem>
          <FormItem name="slotNotes" label={<Text>{t('DESCRIPTION')}</Text>}>
            <TextArea maxLength={250} showCount />
          </FormItem>
        </>
      )}
      {!hideTeamMembers && (
        <FormItem
          name="numberSupply"
          label={<Text>{t('TEAM_MEMBERS_REQUIRED')}</Text>}
          rules={[
            getRequiredRule(t('TEAM_MEMBERS_REQUIRED')),
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    ...getTeamMembersClockInsRule(
                      formData?.status,
                      formData?.clockIns
                    ),
                    validateTrigger: 'onSubmit'
                  }
                ]
              : [])
          ]}
          extra={<Text>{t('TEAM_MEMBERS_REQUIRED_SELECT')}</Text>}
        >
          <InputNumber
            suffix={
              <InputSuffix>{t('TEAM_MEMBERS').toLocaleLowerCase()}</InputSuffix>
            }
            min={1}
            max={50}
            style={{ width: '100%' }}
            type="number"
            formatter={cleanDecimalValues}
          />
        </FormItem>
      )}
      {showTeamMembersSelect && (
        <FormItem
          name="teamMembers"
          rules={[
            ...(flagsRosteringEditingLimitations
              ? [
                  {
                    validator: validateTeamMembers,
                    validateTrigger: ['onSubmit', 'onChange']
                  }
                ]
              : [])
          ]}
          label={<Text>{t('TEAM_MEMBER(S)')}</Text>}
        >
          <TeamMembersMultiSelect
            title={t('SELECT_TEAM_MEMBERS')}
            groupedOptions={groupedCarerOptions}
            formData={formData}
            teamMembersCount={teamMembersCount}
            maxTeamMembers={maxTeamMembers}
            validator={
              flagsRosteringEditingLimitations ? validateTeamMembers : undefined
            }
          />
        </FormItem>
      )}
    </FormCard>
  )
}

export default AboutVisit
